import React from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "16px 0",
  },
  tableContainer: {
    marginBottom: "24px",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: 600,
      fontSize: "0.95rem",
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#f8f9fa",
    },
    "&:hover": {
      backgroundColor: "#e3f2fd",
    },
  },
  divisionCell: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    width: "150px",
  },
  multiplierInput: {
    "& .MuiInputBase-root": {
      height: "40px",
    },
    "& .MuiInputBase-input": {
      textAlign: "center",
      fontWeight: 500,
    },
  },
  multiplierChip: {
    minWidth: "60px",
    fontWeight: 600,
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  helpText: {
    marginTop: "16px",
    padding: "16px",
    backgroundColor: "#f0f7ff",
    borderRadius: "8px",
    border: "1px solid #bbdefb",
  },
}));

const DivisionMultipliersEditor = ({ data, onChange, errors }: any) => {
  const styles = useStyles();

  const handleChange = (division, value) => {
    const newData = {
      ...data,
      [division]: parseFloat(value) || 0,
    };
    onChange(newData);
  };

  const getDivisionName = (division: any) => {
    const names = {
      1: "Platinum",
      2: "Diamond II",
      3: "Diamond",
      4: "Gold II",
      5: "Gold",
      6: "Silver II",
      7: "Silver",
      8: "Bronze II",
      9: "Bronze",
      10: "Starter",
    };
    return names[division] || `Division ${division}`;
  };

  const getDivisionColor = (division: any) => {
    const colors = {
      1: "#9c27b0", // Purple for Platinum
      2: "#3f51b5", // Indigo for Diamond II
      3: "#2196f3", // Blue for Diamond
      4: "#ff9800", // Orange for Gold II
      5: "#ffc107", // Amber for Gold
      6: "#607d8b", // Blue Grey for Silver II
      7: "#9e9e9e", // Grey for Silver
      8: "#795548", // Brown for Bronze II
      9: "#8d6e63", // Light Brown for Bronze
      10: "#4caf50", // Green for Starter
    };
    return colors[division] || "#666";
  };

  // Split divisions into two columns for 5x2 table
  const leftDivisions = [1, 2, 3, 4, 5];
  const rightDivisions = [6, 7, 8, 9, 10];

  return (
    <Box className={styles.container}>
      <TableContainer component={Paper} className={styles.tableContainer}>
        <Table>
          <TableHead className={styles.tableHeader}>
            <TableRow>
              <TableCell>High Tier Divisions</TableCell>
              <TableCell align="center">Multiplier</TableCell>
              <TableCell>Low Tier Divisions</TableCell>
              <TableCell align="center">Multiplier</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leftDivisions.map((leftDiv, index) => {
              const rightDiv = rightDivisions[index];
              return (
                <TableRow key={leftDiv} className={styles.tableRow}>
                  {/* Left Division */}
                  <TableCell className={styles.divisionCell}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: getDivisionColor(leftDiv),
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        Division {leftDiv}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({getDivisionName(leftDiv)})
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent="center"
                    >
                      <TextField
                        type="number"
                        value={data[leftDiv.toString()] || 0}
                        onChange={(e) =>
                          handleChange(leftDiv.toString(), e.target.value)
                        }
                        className={styles.multiplierInput}
                        size="small"
                        inputProps={{
                          min: 0,
                          step: 0.25,
                          style: { width: "70px" },
                        }}
                      />
                      <Chip
                        label={`${data[leftDiv.toString()] || 0}x`}
                        size="small"
                        className={styles.multiplierChip}
                      />
                    </Box>
                  </TableCell>

                  {/* Right Division */}
                  <TableCell className={styles.divisionCell}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: getDivisionColor(rightDiv),
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        Division {rightDiv}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({getDivisionName(rightDiv)})
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent="center"
                    >
                      <TextField
                        type="number"
                        value={data[rightDiv.toString()] || 0}
                        onChange={(e) =>
                          handleChange(rightDiv.toString(), e.target.value)
                        }
                        className={styles.multiplierInput}
                        size="small"
                        inputProps={{
                          min: 0,
                          step: 0.25,
                          style: { width: "70px" },
                        }}
                      />
                      <Chip
                        label={`${data[rightDiv.toString()] || 0}x`}
                        size="small"
                        className={styles.multiplierChip}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {errors && errors.length > 0 && (
        <Box mt={2}>
          {errors.map((error, index) => (
            <Typography key={index} color="error" variant="body2">
              • {error}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DivisionMultipliersEditor;
